import React from 'react';
import { cn } from 'src/lib/utils';

const getPageNumbers = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages = [];
  pages.push(1);
  if (currentPage > 4) pages.push('...');
  const start = Math.max(2, currentPage - 2);
  const end = Math.min(totalPages - 1, currentPage + 2);
  for (let i = start; i <= end; i += 1) pages.push(i);
  if (currentPage < totalPages - 3) pages.push('...');
  pages.push(totalPages);
  return pages;
};

const pgBtnClass = (active, disabled) => {
  if (active)
    return 'bg-primary text-primary-foreground border-primary font-semibold';
  if (disabled)
    return 'bg-muted border-border text-muted-foreground cursor-not-allowed opacity-60';
  return 'bg-card border-border text-muted-foreground hover:border-primary hover:text-primary cursor-pointer';
};

const PgBtn = ({
  onClick,
  disabled,
  active,
  children,
  'aria-label': ariaLabel,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
    aria-current={active ? 'page' : undefined}
    className={cn(
      'w-[30px] h-[30px] rounded-[7px] border inline-flex items-center justify-center text-[13px] transition-colors',
      pgBtnClass(active, disabled)
    )}
  >
    {children}
  </button>
);

const TablePagination = ({
  currentPage = 1,
  totalPages = 1,
  pageSize = 10,
  totalItems = 0,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50],
  itemLabel = 'items',
  className,
}) => {
  const from = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalItems);
  const pages = getPageNumbers(currentPage, totalPages);

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-[14px] pt-[14px] text-[12.5px] text-muted-foreground flex-wrap',
        className
      )}
    >
      {/* Left group: rows per page + showing count */}
      <span className="flex items-center gap-[14px]">
        <span className="flex items-center gap-2">
          Rows per page
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
            className="px-2 py-[5px] border border-border rounded-[7px] font-[inherit] text-[12.5px] bg-card text-foreground-strong focus:outline-none focus:border-primary cursor-pointer"
          >
            {pageSizeOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </span>
        <span>
          Showing{' '}
          <strong className="text-foreground-strong font-semibold">
            {from}–{to}
          </strong>{' '}
          of{' '}
          <strong className="text-foreground-strong font-semibold">
            {totalItems}
          </strong>{' '}
          {itemLabel}
        </span>
      </span>

      {/* Navigation */}
      <span className="flex items-center gap-[6px]">
        <PgBtn
          onClick={() => onPageChange?.(currentPage - 1)}
          disabled={currentPage <= 1}
          aria-label="Previous page"
        >
          ‹
        </PgBtn>

        {pages.map((p) => {
          if (p === '...') {
            return (
              <span
                key={`ellipsis-${p === '...' ? 'after' : 'before'}`}
                className="w-[30px] h-[30px] inline-flex items-center justify-center text-muted-foreground text-[13px]"
              >
                …
              </span>
            );
          }
          return (
            <PgBtn
              key={p}
              onClick={() => onPageChange?.(p)}
              active={p === currentPage}
            >
              {p}
            </PgBtn>
          );
        })}

        <PgBtn
          onClick={() => onPageChange?.(currentPage + 1)}
          disabled={currentPage >= totalPages}
          aria-label="Next page"
        >
          ›
        </PgBtn>
      </span>
    </div>
  );
};

export default TablePagination;
